import { useEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import type { TournamentStatus } from "@alpha/shared";
import { cx } from "../../../lib/cx";
import { STATUS_WORD, type PhaseBox, type PhaseKey } from "../../../lib/tournamentPhases";

/** The console URL for one phase of one tournament. */
export function phasePath(tid: string, key: PhaseKey | "overview"): string {
  return key === "overview" ? `/admin/t/${tid}` : `/admin/t/${tid}/${key}`;
}

type BoxState = "active" | "done" | "attention" | "todo";

interface Cell {
  to: string;
  /** Overview matches only its own path; a phase matches its subtree. */
  end: boolean;
  label: string;
  figure: string | null;
  state: BoxState;
  /** Carried beside `state`, which collapses it: an active box can also need attention. */
  attention: boolean;
}

/**
 * The console's navigation, drawn as a baseball line score (console spec
 * §3.3): one row of joined boxes, a phase name over a live figure. Two things
 * are marked at once and must not be confused — the current ROUTE gets a brand
 * bar under its box, the ACTIVE phase (where Next up points) gets a navy fill —
 * so a reader sees both "you are here" and "the event is here".
 *
 * Overview is the first cell, with the status word as its figure so every cell
 * has the same shape. It passes `end`, or it would read as current on every
 * phase route beneath it.
 */
export function PhaseRail({
  tid,
  boxes,
  active,
  status,
}: {
  tid: string;
  boxes: PhaseBox[];
  active: PhaseKey | null;
  status: TournamentStatus;
}) {
  const { pathname } = useLocation();
  const navRef = useRef<HTMLElement>(null);
  const currentRef = useRef<HTMLAnchorElement>(null);

  // Effects never run under the static test renderer; this is for the phone,
  // where the current box may sit off the right edge of the strip. Guarded on
  // the strip actually overflowing, so a laptop never has the page scrolled
  // for a row that was already in full view.
  useEffect(() => {
    const nav = navRef.current;
    if (!nav || nav.scrollWidth <= nav.clientWidth) return;
    currentRef.current?.scrollIntoView({ inline: "center", block: "nearest" });
  }, [pathname]);

  const cells: Cell[] = [
    {
      to: phasePath(tid, "overview"),
      end: true,
      label: "Overview",
      figure: STATUS_WORD[status],
      state: status === "complete" ? "done" : "todo",
      attention: false,
    },
    ...boxes.map(
      (b): Cell => ({
        to: phasePath(tid, b.key),
        end: false,
        label: b.label,
        figure: b.figure,
        state: active === b.key ? "active" : b.done ? "done" : b.attention ? "attention" : "todo",
        attention: b.attention,
      }),
    ),
  ];

  // The same test NavLink applies for `aria-current`, so the ref lands on the
  // anchor that is marked current.
  const here = pathname.replace(/\/+$/, "");
  const isCurrent = (c: Cell) => (c.end ? here === c.to : here === c.to || here.startsWith(`${c.to}/`));

  return (
    // No bleed past the page gutter: a padded scroller pins a sticky cell at
    // its content edge, and the cells scrolling under it would show through
    // the padding beside it. The strip scrolls inside the content box instead,
    // and each cell carries its own border so the pinned one stays framed
    // after the row's left edge has scrolled away.
    <nav
      ref={navRef}
      aria-label="Tournament phases"
      className="snap-x snap-proximity overflow-x-auto pb-2"
    >
      <div className="inline-flex min-w-full rounded-xl bg-white shadow-card">
        {cells.map((c, i) => (
          <NavLink
            key={c.to}
            to={c.to}
            end={c.end}
            ref={isCurrent(c) ? currentRef : undefined}
            data-state={c.state}
            className={({ isActive }) =>
              cx(
                // `flex-1` with `shrink-0`: a short rail (a scorer's two cells) fills
                // the row, a full one overflows at its minimum widths and scrolls.
                "relative flex min-w-[7.5rem] shrink-0 snap-start flex-col gap-0.5 border-y border-r px-3 py-2 transition-colors first:rounded-l-xl first:border-l last:rounded-r-xl flex-1",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-600",
                // Overview stays pinned while the strip scrolls, so the way
                // back is always on screen.
                i === 0 && "sticky left-0 z-10",
                c.state === "active"
                  ? "border-navy-900 bg-navy-900 text-white hover:bg-navy-800"
                  : "border-slate-200 bg-white text-navy-950 hover:bg-slate-50",
                // The current-route bar sits in the gap below the row, never on
                // the box's own background, so it survives a navy fill and any
                // brand color an admin picks.
                isActive &&
                  "after:absolute after:inset-x-0 after:-bottom-2 after:h-0.5 after:rounded-full after:bg-brand-600",
              )
            }
          >
            <span className="font-display text-[13px] font-semibold leading-tight">
              {c.label}
              {c.state === "done" ? (
                <>
                  <span className="sr-only">, done</span>
                  <span aria-hidden="true"> ✓</span>
                </>
              ) : null}
            </span>
            <span
              className={cx(
                "text-xs tabular-nums leading-tight",
                c.state === "active"
                  ? c.attention
                    ? "text-amber-300"
                    : "text-navy-200"
                  : c.attention
                    ? "text-amber-700"
                    : "text-slate-500",
              )}
            >
              {c.figure ?? " "}
            </span>
          </NavLink>
        ))}
      </div>
    </nav>
  );
}
